import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  TrendingUp,
  Upload,
  Calendar,
  RefreshCw,
  IndianRupee,
  Package,
  Users,
  BarChart3,
  Search,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle2,
  ChevronRight,
  ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import { DMYDateInput } from "@/components/ui/dmy-date-picker";

import { formatDMY } from "@/lib/dmy";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PartnerDashboard() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [salesData, setSalesData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCode, setSelectedCode] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  
  // Upload dialog
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadDate, setUploadDate] = useState("");
  const [uploading, setUploading] = useState(false);
  
  // View mode
  const [viewMode, setViewMode] = useState("overview"); // overview, sales, code-detail
  const [selectedCodeDetail, setSelectedCodeDetail] = useState(null);

  // Update page title
  useEffect(() => {
    document.title = `Partner Dashboard - ${user?.client_code || ""} | EAC`;
    return () => { document.title = "EAC"; };
  }, [user]);

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/partner/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setDashboardData(data);
      } else {
        toast.error(data.detail || "Failed to load dashboard");
      }
    } catch (error) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch sales data
  const fetchSales = useCallback(async () => {
    try {
      let url = `${BACKEND_URL}/api/partner/sales`;
      const params = new URLSearchParams();
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (selectedCode && selectedCode !== "all") params.append("code", selectedCode);
      
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setSalesData(data.sales || []);
      }
    } catch (error) {
      console.error("Error fetching sales:", error);
    }
  }, [token, dateFrom, dateTo, selectedCode]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    if (dashboardData) {
      fetchSales();
    }
  }, [dashboardData, fetchSales]);

  // Handle file upload
  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!uploadDate) {
      toast.error("Please enter the upload date (ddmmyyyy)");
      return;
    }
    
    // Validate date format
    if (!/^\d{8}$/.test(uploadDate)) {
      toast.error("Invalid date format. Use ddmmyyyy (e.g., 15012025)");
      return;
    }
    
    // Check if date already exists
    if (dashboardData?.existing_upload_dates?.includes(uploadDate)) {
      toast.error(`Data for date ${uploadDate} already exists. Please choose a different date.`);
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_date", uploadDate);
      
      const response = await fetch(`${BACKEND_URL}/api/partner/upload-sales`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        setShowUploadDialog(false);
        setUploadDate("");
        fetchDashboard();
        fetchSales();
      } else {
        toast.error(data.detail || "Upload failed");
      }
    } catch (error) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  // Sales by code
  const salesByCode = useMemo(() => {
    const grouped = {};
    salesData.forEach(sale => {
      const code = sale.client_code || "Unknown";
      if (!grouped[code]) {
        grouped[code] = { code, orders: 0, total_sales: 0, total_profit: 0 };
      }
      grouped[code].orders += 1;
      grouped[code].total_sales += sale.sell_price || 0;
      grouped[code].total_profit += sale.profit || 0;
    });
    return Object.values(grouped).sort((a, b) => b.total_sales - a.total_sales);
  }, [salesData]);

  // Filtered sales
  const filteredSales = useMemo(() => {
    return salesData.filter(s => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (
        s.tracking_number?.toLowerCase().includes(term) ||
        s.client_code?.toLowerCase().includes(term)
      );
    });
  }, [salesData, searchTerm]);

  // All available codes
  const allCodes = useMemo(() => {
    if (!dashboardData) return [];
    return [dashboardData.partner_code, ...(dashboardData.under_codes || [])];
  }, [dashboardData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <TrendingUp className="w-7 h-7 text-blue-600" />
              Partner Dashboard
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Welcome, {user?.display_name || user?.username} ({dashboardData?.partner_code})
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => { fetchDashboard(); fetchSales(); }}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => setShowUploadDialog(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Upload Sales
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-5 border shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Orders</p>
                <p className="text-2xl font-bold">{dashboardData?.summary?.total_orders || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <IndianRupee className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Sales</p>
                <p className="text-2xl font-bold">{formatCurrency(dashboardData?.summary?.total_sales)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Profit</p>
                <p className="text-2xl font-bold">{formatCurrency(dashboardData?.summary?.total_profit)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Users className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Under Codes</p>
                <p className="text-2xl font-bold">{dashboardData?.under_codes?.length || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="sales">Sales Records</TabsTrigger>
            <TabsTrigger value="codes">Under Codes</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-2 gap-6">
              {/* Sales by Code */}
              <div className="bg-white rounded-xl p-5 border">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Sales by Code
                </h3>
                {salesByCode.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-8">No sales data available</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {salesByCode.map(item => (
                      <div key={item.code} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer">
                        <div>
                          <span className="font-medium">{item.code}</span>
                          <span className="text-sm text-slate-500 ml-2">({item.orders} orders)</span>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-green-600">{formatCurrency(item.total_sales)}</p>
                          <p className="text-xs text-slate-500">Profit: {formatCurrency(item.total_profit)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Upload Status */}
              <div className="bg-white rounded-xl p-5 border">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Upload History
                </h3>
                {(dashboardData?.existing_upload_dates || []).length === 0 ? (
                  <div className="text-center py-8">
                    <FileSpreadsheet className="w-12 h-12 text-slate-300 mx-auto" />
                    <p className="text-sm text-slate-500 mt-2">No uploads yet</p>
                    <Button variant="outline" size="sm" className="mt-4" onClick={() => setShowUploadDialog(true)}>
                      Upload First Sheet
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {dashboardData.existing_upload_dates.map(date => (
                      <div key={date} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        <span className="font-mono">{date}</span>
                        <span className="text-sm text-green-600">Uploaded</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Sales Records Tab */}
          <TabsContent value="sales" className="space-y-4">
            {/* Filters */}
            <div className="flex items-center gap-4 bg-white p-4 rounded-lg border">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by tracking or code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={selectedCode} onValueChange={setSelectedCode}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Filter by code" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Codes</SelectItem>
                  {allCodes.map(code => (
                    <SelectItem key={code} value={code}>{code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <DMYDateInput value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" placeholder="From" />
              <DMYDateInput value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" placeholder="To" />
            </div>

            {/* Sales Table */}
            <div className="bg-white rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Tracking</TableHead>
                    <TableHead>Client Code</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Sell Price</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead className="text-right">Profit</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSales.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                        No sales records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSales.slice(0, 100).map((sale, idx) => (
                      <TableRow key={sale.id || idx} className="hover:bg-slate-50">
                        <TableCell className="text-slate-500">{idx + 1}</TableCell>
                        <TableCell className="font-mono text-sm">{sale.tracking_number}</TableCell>
                        <TableCell className="font-medium">{sale.client_code}</TableCell>
                        <TableCell className="text-sm">{formatDMY(sale.order_date)}</TableCell>
                        <TableCell className="text-right font-semibold text-green-600">
                          {formatCurrency(sale.sell_price)}
                        </TableCell>
                        <TableCell className="text-right text-slate-600">
                          {formatCurrency(sale.cost_price)}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-purple-600">
                          {formatCurrency(sale.profit)}
                        </TableCell>
                        <TableCell>
                          <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                            {sale.status || "active"}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {filteredSales.length > 100 && (
                <div className="p-3 text-center text-sm text-slate-500 bg-slate-50">
                  Showing 100 of {filteredSales.length} records
                </div>
              )}
            </div>
          </TabsContent>

          {/* Under Codes Tab */}
          <TabsContent value="codes" className="space-y-4">
            <div className="bg-white rounded-lg border">
              {(dashboardData?.under_codes || []).length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Users className="w-12 h-12 text-slate-300" />
                  <p className="text-slate-500 mt-4">No under codes assigned</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead className="text-right">Orders</TableHead>
                      <TableHead className="text-right">Total Sales</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dashboardData.under_codes.map((code, idx) => {
                      const codeData = salesByCode.find(s => s.code === code) || { orders: 0, total_sales: 0 };
                      return (
                        <TableRow key={code} className="hover:bg-slate-50">
                          <TableCell className="text-slate-500">{idx + 1}</TableCell>
                          <TableCell className="font-medium">{code}</TableCell>
                          <TableCell className="text-right">{codeData.orders}</TableCell>
                          <TableCell className="text-right font-semibold text-green-600">
                            {formatCurrency(codeData.total_sales)}
                          </TableCell>
                          <TableCell>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => { setSelectedCode(code); }}
                            >
                              <ChevronRight className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload Sales Sheet
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Date-based Upload</p>
                  <p className="text-sm text-amber-600">
                    Each upload must have a unique date. If data for a date already exists, you cannot upload again for that date.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Upload Date (ddmmyyyy) *</Label>
              <Input
                value={uploadDate}
                onChange={(e) => setUploadDate(e.target.value)}
                placeholder="e.g., 15012025"
                maxLength={8}
              />
              <p className="text-xs text-slate-500">Format: Day(2) + Month(2) + Year(4)</p>
            </div>

            {dashboardData?.existing_upload_dates?.includes(uploadDate) && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                ⚠️ Data for this date already exists!
              </div>
            )}

            <div className="space-y-2">
              <Label>Excel File *</Label>
              <div className="border-2 border-dashed rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleUpload}
                  className="hidden"
                  id="sales-upload"
                  disabled={uploading || !uploadDate || dashboardData?.existing_upload_dates?.includes(uploadDate)}
                />
                <label htmlFor="sales-upload" className="cursor-pointer">
                  <FileSpreadsheet className="w-10 h-10 mx-auto text-slate-400" />
                  <p className="mt-2 text-sm">
                    {uploading ? "Uploading..." : "Click to select Excel file"}
                  </p>
                  <p className="text-xs text-slate-400">.xlsx or .xls</p>
                </label>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm font-medium text-blue-800">Expected Columns:</p>
              <p className="text-xs text-blue-600">Tracking Number, Sell Price</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
